import { format } from "date-fns";
import React from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useI18n } from "@/context";

import { TSale } from "../../reports.types";

interface ISaleDetailsModalProps {
  sale: TSale | null;
  isOpen: boolean;
  onClose: () => void;
}

export const SaleDetailsModal: React.FC<ISaleDetailsModalProps> = ({
  sale,
  isOpen,
  onClose,
}) => {
  const { t } = useI18n();

  if (!sale) return null;

  const formatCurrency = (value: number) => {
    // Converte de centavos para reais
    const valueInReais = value / 100;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valueInReais);
  };

  const formatPhone = (phone: string) => {
    // Formata telefone: 11987654321 -> (11) 98765-4321
    if (phone.length === 11) {
      return `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7)}`;
    }
    return phone;
  };

  const formatZipCode = (zip: string) => {
    // Formata CEP: 01310100 -> 01310-100
    if (zip.length === 8) {
      return `${zip.slice(0, 5)}-${zip.slice(5)}`;
    }
    return zip;
  };

  const stripHtml = (html: string) => {
    // Remove tags HTML e retorna texto puro
    if (!html) return "";
    
    // Cria um elemento temporário para parsear o HTML
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!max-w-[80vw] !w-[80vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold mb-4">
            {t("reports.details.title")} - {sale.id}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-8">
          {/* Informações da Venda */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-primary">
              {t("reports.details.saleInfo")}
            </h3>
            <div className="grid grid-cols-4 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.table.saleId")}
                </p>
                <p className="font-medium">{sale.id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.table.saleDate")}
                </p>
                <p className="font-medium">
                  {format(new Date(sale.sale_date), "dd/MM/yyyy")}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.table.partnerName")}
                </p>
                <p className="font-medium">{sale.partner_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.table.salePrice")}
                </p>
                <p className="font-medium">{formatCurrency(sale.sale_price)}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Dados do Cliente */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-primary">
              {t("reports.details.clientInfo")}
            </h3>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.details.fullName")}
                </p>
                <p className="font-medium">{sale.client_details.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.details.email")}
                </p>
                <p className="font-medium">{sale.client_details.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.details.cpf")}
                </p>
                <p className="font-medium">{sale.client_cpf}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.details.cellphone")}
                </p>
                <p className="font-medium">
                  {formatPhone(sale.client_details.cellphone)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.details.birthdate")}
                </p>
                <p className="font-medium">
                  {format(new Date(sale.client_details.birthdate), "dd/MM/yyyy")}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.details.gender")}
                </p>
                <p className="font-medium">
                  {sale.client_details.gender === "M"
                    ? t("reports.details.male")
                    : t("reports.details.female")}
                </p>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-lg font-semibold mb-3">
                {t("reports.details.address")}
              </h4>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("reports.details.zipCode")}
                  </p>
                  <p className="font-medium">
                    {formatZipCode(sale.client_details.zip)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("reports.details.street")}
                  </p>
                  <p className="font-medium">{sale.client_details.address}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("reports.details.number")}
                  </p>
                  <p className="font-medium">{sale.client_details.number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("reports.details.complement")}
                  </p>
                  <p className="font-medium">
                    {sale.client_details.complement || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("reports.details.district")}
                  </p>
                  <p className="font-medium">{sale.client_details.district}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("reports.details.city")}
                  </p>
                  <p className="font-medium">
                    {sale.client_details.city} - {sale.client_details.state}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Detalhes do Plano */}
          <div>
            <h3 className="text-xl font-bold mb-4 text-primary">
              {t("reports.details.planInfo")}
            </h3>
            <div className="grid grid-cols-3 gap-6 mb-6">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.details.planName")}
                </p>
                <p className="font-medium">{sale.plan_details.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.details.planId")}
                </p>
                <p className="font-medium">{sale.plan_details.id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("reports.details.planDescription")}
                </p>
                <p className="font-medium whitespace-pre-wrap">{stripHtml(sale.plan_details.description)}</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold mb-4">
              {t("reports.details.products")}
            </h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("reports.details.productName")}</TableHead>
                  <TableHead>{t("reports.filters.supplier")}</TableHead>
                  <TableHead>{t("reports.details.productDescription")}</TableHead>
                  <TableHead className="text-right">
                    {t("reports.details.productPrice")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sale.plan_details.products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>
                      {product.insurance_company_name?.trim() || "—"}
                    </TableCell>
                    <TableCell className="whitespace-pre-wrap">{stripHtml(product.description)}</TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(product.price)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/50">
                  <TableCell colSpan={3} className="font-semibold">
                    {t("reports.details.total")}
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    {formatCurrency(sale.sale_price)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

